// src/pages/SuperAdmin/Packages.jsx - SUPER OPTIMIZED

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, X, Loader } from 'lucide-react';
import StorageService from '../../services/StorageService';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';

const SuperAdminPackages = () => {
  const [packages, setPackages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({ name: '', price: '', features: '', qrLimit: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPackages();
  }, []);

  // ✅ OPTIMIZED: Load only packages from cache if available
  const loadPackages = async () => {
    try {
      setLoading(true);
      console.log('📦 Loading packages...');
      
      const data = await StorageService.getData();
      console.log('📦 Packages loaded:', data.packages);
      
      setPackages(data.packages || []);
    } catch (error) {
      console.error('❌ Error loading packages:', error);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ OPTIMIZED: Direct Firestore write + optimistic UI update
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      const featuresArray = formData.features.split(',').map(f => f.trim());
      
      if (editingPackage) {
        // ✅ Update existing package
        const packageId = editingPackage.id;
        const updatedPackage = {
          id: packageId,
          name: formData.name,
          price: Number(formData.price),
          features: featuresArray,
          qrLimit: Number(formData.qrLimit)
        };

        // ✅ OPTIMISTIC UPDATE - Update UI immediately
        setPackages(prevPackages => 
          prevPackages.map(p => 
            p.id === packageId ? updatedPackage : p
          )
        );

        // ✅ Save to Firestore in background
        const packageRef = doc(db, 'packages', packageId);
        await setDoc(packageRef, updatedPackage, { merge: true });
        
        console.log('✅ Package updated:', packageId);
      } else {
        // ✅ Add new package
        const newPackageId = `pkg_${Date.now()}`;
        const newPackage = {
          id: newPackageId,
          name: formData.name,
          price: Number(formData.price),
          features: featuresArray,
          qrLimit: Number(formData.qrLimit)
        };

        // ✅ OPTIMISTIC UPDATE - Add to UI immediately
        setPackages(prevPackages => [...prevPackages, newPackage]);

        // ✅ Save to Firestore in background
        const packageRef = doc(db, 'packages', newPackageId);
        await setDoc(packageRef, newPackage);
        
        console.log('✅ Package created:', newPackageId);
      }

      // ✅ Clear cache to ensure fresh data on next full load
      StorageService.clearCache();
      
      setShowModal(false);
      setEditingPackage(null);
      setFormData({ name: '', price: '', features: '', qrLimit: '' });
      
      alert('✅ Package saved successfully!');
    } catch (error) {
      console.error('❌ Error saving package:', error);
      alert('Failed to save package. Please try again.');
      // ✅ Reload on error
      await loadPackages();
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = useCallback((pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      price: pkg.price.toString(),
      maxKitchenDevices: 2 ,
      features: pkg.features.join(', '),
      qrLimit: pkg.qrLimit?.toString() || '10'
    });
    setShowModal(true);
  }, []);

  // ✅ OPTIMIZED: Direct Firestore delete + optimistic UI update
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      try {
        setSaving(true);
        console.log('🗑️ Deleting package:', id);
        
        // ✅ OPTIMISTIC UPDATE - Remove from UI immediately
        setPackages(prevPackages => prevPackages.filter(p => p.id !== id));
        
        // ✅ Delete from Firestore in background
        const packageRef = doc(db, 'packages', id);
        await deleteDoc(packageRef);
        
        // ✅ Clear cache
        StorageService.clearCache();
        
        console.log('✅ Package deleted');
        alert('✅ Package deleted successfully!');
      } catch (error) {
        console.error('❌ Error deleting package:', error);
        alert('Failed to delete package. Please try again.');
        // ✅ Reload on error
        await loadPackages();
      } finally {
        setSaving(false);
      }
    }
  };

  // ✅ OPTIMIZED: Memoize form handlers
  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingPackage(null);
    setFormData({ name: '', price: '', features: '', qrLimit: '' });
  }, []);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading packages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Packages</h1>
        <button
          onClick={() => setShowModal(true)}
          disabled={saving}
          className="bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 flex items-center gap-2 disabled:opacity-50 transition"
        >
          <Plus className="w-5 h-5" /> Add Package
        </button>
      </div>

      {packages.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <p className="text-gray-500 mb-4">No packages found</p>
          <button
            onClick={() => setShowModal(true)}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600"
          >
            Create Your First Package
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map(pkg => (
            <div key={pkg.id} className="bg-white rounded-xl shadow-md p-6 border-t-4 border-orange-500 hover:shadow-lg transition">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
              <p className="text-3xl font-bold text-orange-500 mb-4">₹{pkg.price}/month</p>
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800 font-semibold">QR Tables Limit</p>
                <p className="text-2xl font-bold text-blue-600">{pkg.qrLimit || 10} Tables</p>
              </div>
              <ul className="space-y-2 mb-6">
                {pkg.features && pkg.features.map((feature, idx) => (
                  <li key={idx} className="text-gray-600 flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(pkg)}
                  disabled={saving}
                  className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(pkg.id)}
                  disabled={saving}
                  className="flex-1 bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 disabled:opacity-50 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingPackage ? 'Edit Package' : 'Add Package'}
              </h2>
              <button 
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700"
                disabled={saving}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Package Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  required
                  disabled={saving}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price (₹/month)</label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleFormChange('price', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  required
                  disabled={saving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">QR Tables Limit</label>
                <input
                  type="number"
                  value={formData.qrLimit}
                  onChange={(e) => handleFormChange('qrLimit', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="e.g., 10, 20, 30"
                  required
                  disabled={saving}
                />
                <p className="text-xs text-gray-500 mt-1">Number of QR code tables this package allows</p>
              </div>

              <div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    Max Kitchen Staff Devices
  </label>
  <input
    type="number"
    value={formData.maxKitchenDevices}
    onChange={(e) => setFormData({...formData, maxKitchenDevices: parseInt(e.target.value)})}
    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
    min="1"
    max="10"
    required
  />
  <p className="text-xs text-gray-500 mt-1">
    Number of simultaneous kitchen staff logins allowed
  </p>
</div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Features (comma-separated)</label>
                <textarea
                  value={formData.features}
                  onChange={(e) => handleFormChange('features', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  rows="3"
                  placeholder="50 Menu Items, 5GB Storage, Analytics"
                  required
                  disabled={saving}
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2 transition"
              >
                {saving ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>{editingPackage ? 'Update' : 'Create'} Package</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminPackages;